import { fileViewerAtom, previewKeyAtom, shallowEqualFileViewerAtom, } from 'js/file_viewer/core/atoms/atom';
import { rightSidebarIdAtom } from 'js/file_viewer/core/atoms/plugins';
import { getPreviewKeyForNestedArchiveFile } from 'js/file_viewer/core/data/preview_key';
import { TRANSCRIPT_BLADE_PLUGIN_ID } from 'js/file_viewer/core/data/types';
import { archiveSubpathQueryParam } from 'js/file_viewer/core/middlewares/url';
/**
 * Current fileInfo for each File Viewer instance. This is unique to each root level file. That means that this will
 * not change as the user navigates through the sub-files of an archive file.
 */
export const rootFileInfoAtom = fileViewerAtom(undefined, (get, set, newRootFileInfo) => {
    const previousValue = get(rootFileInfoAtom);
    set(rootFileInfoAtom, newRootFileInfo);
    /*
     * If the file info changes from one file to another, we assume the user is looking at a new file. In that case,
     * we want to close the transcript sidebar if it's open. Otherwise, a user flipping through a directory of videos
     * with the transcript sidebar open could overwhelm the transcription service.
     */
    const isTranscriptShowing = get(rightSidebarIdAtom) === TRANSCRIPT_BLADE_PLUGIN_ID;
    if (previousValue !== undefined && isTranscriptShowing) {
        set(rightSidebarIdAtom, undefined);
    }
});
export const rootFileMetadataAtom = fileViewerAtom(undefined);
export const baseFileAtom = shallowEqualFileViewerAtom((get) => {
    const fileInfo = get(rootFileInfoAtom);
    const fileMetadata = get(rootFileMetadataAtom);
    if (!fileInfo || !fileMetadata) {
        return null;
    }
    const { file_id: fileId, ns_id: nsId, sj_id: sjId, url } = fileInfo;
    if (fileId == null || nsId == null || sjId == null) {
        return null;
    }
    const { file_name: name, file_path: fqPath, file_last_modified_date: lastModifiedDate, size, readOnly, } = fileMetadata;
    return {
        fileId,
        nsId: nsId,
        sjId: sjId,
        url,
        lastModifiedDate,
        name,
        fqPath,
        size,
        readOnly,
    };
}, null);
export const sharedLinkInfoAtom = fileViewerAtom(undefined);
export const rootPreviewKeyAtom = shallowEqualFileViewerAtom((get) => {
    const fileInfo = get(rootFileInfoAtom);
    return fileInfo === null || fileInfo === void 0 ? void 0 : fileInfo.previewKey;
}, undefined);
/**
 * A key that uniquely identifies the current preview. It combines file ID, nsid, sjid, and shared link URL (if any).
 *
 * For non-archive files, this identifies the file. For archive previews, it identifies the archive itself when the
 * user is looking at the root of the archive. As they navigate into the archive, the preview key will change to
 * identify the file they are looking at.
 */
export const currentPreviewKeyAtom = shallowEqualFileViewerAtom((get) => {
    const rootFile = get(rootFileInfoAtom);
    if (!rootFile) {
        return undefined;
    }
    const concatenatedPath = concatenateArchivePath(get(currentArchivePathAtom));
    return concatenatedPath.length === 0 || get(isInArchiveSubdirectoryAtom)
        ? rootFile.previewKey
        : getPreviewKeyForNestedArchiveFile(rootFile.file_id, concatenatedPath);
}, undefined);
/**
 * Archive atoms. Stored here because there's a necessary cycle between currentPreviewKeyAtom and
 * currentArchivePathAtom.
 */
export const allArchiveSubpathsAtom = previewKeyAtom([]);
export const concatenateArchivePath = (path) => {
    let stringPath = '';
    if (path.length) {
        stringPath += `/${path.join('/')}`;
    }
    return `${stringPath}`;
};
// These are stored a pair because the timing of when currentPath and isDir are updated matters to
// rendering. By updating them at the same time, we can ensure that the UI is always in a consistent state.
const currentArchivePathAndIsDirInternalAtom = previewKeyAtom({
    currentPath: [],
    isDir: false,
});
export const currentArchiveFolderAtom = fileViewerAtom((get) => {
    const isInSubdir = get(isInArchiveSubdirectoryAtom);
    const currentPath = get(currentArchivePathAtom);
    return concatenateArchivePath(isInSubdir ? currentPath : currentPath.slice(0, currentPath.length - 1));
});
const archiveCurrentPathToQueryStringValue = (currentPath) => {
    return currentPath.length === 0 ? null : `/${currentPath.join('/')}`;
};
const isInArchiveSubdirectoryAtom = fileViewerAtom((get) => {
    const rootPreviewKey = get(rootPreviewKeyAtom);
    return get(currentArchivePathAndIsDirInternalAtom(rootPreviewKey)).isDir;
});
export const currentArchivePathAtom = fileViewerAtom((get) => {
    const rootPreviewKey = get(rootPreviewKeyAtom);
    return get(currentArchivePathAndIsDirInternalAtom(rootPreviewKey)).currentPath;
}, (get, set, newVal, urlClient, payload) => {
    const rootPreviewKey = get(rootPreviewKeyAtom);
    if (!payload.fromUrlChanged && urlClient) {
        urlClient.setQueryStringParameter(archiveSubpathQueryParam, archiveCurrentPathToQueryStringValue(newVal));
    }
    set(currentArchivePathAndIsDirInternalAtom(rootPreviewKey), {
        currentPath: newVal,
        isDir: payload.isDir,
    });
});
//# sourceMappingURL=metadata.preenvesnextjs.map
